/**
 * Blog post definitions — static defaults + dynamic KV-backed CRUD.
 *
 * Default posts serve as initial content for new templates.
 * When the user adds/edits/deletes posts through edit mode,
 * the changes are persisted to Supabase KV under `blog:dynamic-posts`.
 */

import { useCallback, useMemo } from "react";
import { useContentEditingSafe } from "../components/shared/ContentEditingContext";

export interface BlogPost {
  title: string;
  excerpt: string;
  category: string;
  slug: string;
  date: string;
  author: string;
  /** Markdown/plain body content for the post page */
  body?: string;
}

/** Static default posts — used when no KV overrides exist */
export const defaultPosts: BlogPost[] = [
  {
    title: "כותרת מאמר ראשון — מדריך מקצועי בתחום שלכם",
    excerpt: "תקציר המאמר. כתבו 2-3 משפטים שנותנים טעימה מהתוכן ומעודדים את הקורא ללחוץ. המאמר צריך לספק ערך אמיתי לקהל היעד.",
    category: "מדריכים",
    slug: "post-1",
    date: "15 בינואר, 2026",
    author: "צוות המומחים",
    body: "כאן יופיע תוכן המאמר המלא. פסקה ראשונה שמספרת על הנושא הכללי ומסבירה למה הוא חשוב.\n\nפסקה שנייה שנכנסת לפרטים — טכניקות, טיפים, שלבים. ממש כמו מדריך מקצועי שנותן ערך אמיתי לקורא.\n\nפסקה שלישית עם סיכום ומסקנות. מה הקורא צריך לעשות עכשיו? מהם הצעדים הבאים?",
  },
  {
    title: "כותרת מאמר שני — טרנדים ועדכונים",
    excerpt: "תקציר של מאמר על מגמות בתעשייה. חדשות, טכנולוגיות חדשות, או שינויים בשוק שהלקוחות שלכם צריכים לדעת עליהם.",
    category: "חדשות",
    slug: "post-2",
    date: "10 בינואר, 2026",
    author: "צוות המומחים",
    body: "מאמר על מגמות ושינויים בתעשייה. מה חדש? מה השתנה? מה הלקוחות שלכם צריכים לדעת?\n\nהסבירו את ההשפעות של השינויים האלה על הענף שלכם ועל הלקוחות.\n\nסיימו עם תחזיות — מה צפוי לקרות בעתיד הקרוב?",
  },
  {
    title: "כותרת מאמר שלישי — טיפים מעשיים",
    excerpt: "תקציר של מאמר עם טיפים פרקטיים. רשימות, המלצות, או שלבים שהלקוח יכול ליישם בעצמו. תוכן שמייצר אמון.",
    category: "טיפים",
    slug: "post-3",
    date: "5 בינואר, 2026",
    author: "צוות המומחים",
    body: "טיפ ראשון: הסבירו את הטיפ בצורה ברורה ופרקטית.\n\nטיפ שני: תנו דוגמה מהחיים שממחישה את הנקודה.\n\nטיפ שלישי: סיימו עם קריאה לפעולה — מה הקורא צריך לעשות עכשיו?",
  },
  {
    title: "כותרת מאמר רביעי — מקרה בוחן",
    excerpt: "תקציר של מאמר case study. ספרו על פרויקט ספציפי — מה האתגר, מה עשיתם, ומה התוצאה. תוכן שמייצר אמינות.",
    category: "מקרי בוחן",
    slug: "post-4",
    date: "1 בינואר, 2026",
    author: "צוות המומחים",
    body: "האתגר: תארו את הבעיה שהלקוח הביא אליכם.\n\nהפתרון: מה עשיתם? אילו שירותים או מוצרים הפעלתם?\n\nהתוצאה: מה השתנה? מספרים, אחוזים, ציטוטים מהלקוח.",
  },
  {
    title: "כותרת מאמר חמישי — שאלות ותשובות",
    excerpt: "תקציר של מאמר Q&A. ענו על שאלות נפוצות בפורמט נגיש וידידותי. תוכן שעוזר לקידום בגוגל.",
    category: "שאלות נפוצות",
    slug: "post-5",
    date: "25 בדצמבר, 2025",
    author: "צוות המומחים",
    body: "שאלה: מה הנושא הנפוץ ביותר שנשאלים?\nתשובה: ענו בצורה ברורה ומפורטת.\n\nשאלה: מה ההבדל בין אפשרות א׳ לאפשרות ב׳?\nתשובה: השוו ותנו המלצה.\n\nשאלה: כמה זה עולה?\nתשובה: תנו טווח מחירים או הפנו לעמוד צור קשר.",
  },
  {
    title: "כותרת מאמר שישי — השוואה מקצועית",
    excerpt: "תקציר של מאמר השוואתי. השוו בין אפשרויות, מוצרים, או גישות שונות. תוכן שעוזר ללקוח לקבל החלטה.",
    category: "השוואות",
    slug: "post-6",
    date: "20 בדצמבר, 2025",
    author: "צוות המומחים",
    body: "אפשרות א׳: תארו את היתרונות והחסרונות.\n\nאפשרות ב׳: תארו את היתרונות והחסרונות.\n\nסיכום: מה מתאים למי? תנו המלצה מותאמת לסוגי לקוחות שונים.",
  },
];

/** KV key for the dynamic posts list */
const DYNAMIC_POSTS_KEY = "blog:dynamic-posts";

/**
 * Hook: useDynamicPosts — reads/writes blog posts from KV.
 * Returns the merged list of posts (KV overrides > defaults).
 */
export function useDynamicPosts() {
  const ctx = useContentEditingSafe();
  const getContent = ctx?.getContent ?? ((_k: string, d: string) => d);
  const saveContent = ctx?.saveContent ?? (() => {});
  const isEditMode = ctx?.isEditMode ?? false;

  // Read dynamic posts from KV; if none, use defaults
  const posts: BlogPost[] = useMemo(() => {
    try {
      const raw = getContent(DYNAMIC_POSTS_KEY, "");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed) && parsed.length > 0) return parsed;
      }
    } catch { /* use defaults */ }
    return defaultPosts;
  }, [getContent]);

  const savePosts = useCallback(
    (newPosts: BlogPost[]) => {
      saveContent(DYNAMIC_POSTS_KEY, JSON.stringify(newPosts));
    },
    [saveContent]
  );

  const addPost = useCallback(
    (post: Omit<BlogPost, "slug">) => {
      const slug = `post-${Date.now().toString(36)}`;
      const newPost: BlogPost = { ...post, slug };
      const updated = [newPost, ...posts];
      savePosts(updated);
      return slug;
    },
    [posts, savePosts]
  );

  const updatePost = useCallback(
    (slug: string, changes: Partial<BlogPost>) => {
      const updated = posts.map((p) =>
        p.slug === slug ? { ...p, ...changes } : p
      );
      savePosts(updated);
    },
    [posts, savePosts]
  );

  const deletePost = useCallback(
    (slug: string) => {
      const updated = posts.filter((p) => p.slug !== slug);
      savePosts(updated);
    },
    [posts, savePosts]
  );

  const reorderPosts = useCallback(
    (fromIndex: number, toIndex: number) => {
      const updated = [...posts];
      const [moved] = updated.splice(fromIndex, 1);
      updated.splice(toIndex, 0, moved);
      savePosts(updated);
    },
    [posts, savePosts]
  );

  return {
    posts,
    addPost,
    updatePost,
    deletePost,
    reorderPosts,
    isEditMode,
  };
}

/** Get unique categories from all posts */
export function getCategories(): string[] {
  return [...new Set(defaultPosts.map((p) => p.category))];
}

/** Get categories from a dynamic posts list */
export function getCategoriesFromPosts(posts: BlogPost[]): string[] {
  return [...new Set(posts.map((p) => p.category))];
}

/** Find a post by slug from the defaults list (legacy support) */
export function findPostBySlug(slug: string): BlogPost | undefined {
  return defaultPosts.find((p) => p.slug === slug);
}

/** Get recent posts (exclude current, take up to count) */
export function getRecentPosts(currentSlug: string, count = 5): BlogPost[] {
  return defaultPosts.filter((p) => p.slug !== currentSlug).slice(0, count);
}
